import { AxiosError } from 'axios';
import * as React from 'react';
import styled from 'styled-components';

import useProgressBar from 'src/CustomHooks/useProgressBar';
import ISubmission from 'src/ServerEntities/Submission';
import AttributionContainer from 'src/SharedComponents/AttributionContainer';
import Button from 'src/SharedComponents/Button';
import ButtonContainer from 'src/SharedComponents/ButtonContainer';
import ContentWrapper from 'src/SharedComponents/ContentWrapper';
import ErrorToast from 'src/SharedComponents/ErrorToast';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import ProgressBar from 'src/SharedComponents/ProgressBar';
import QuestionList from 'src/SharedComponents/QuestionList';

import UnlabeledMultipleChoiceQuestion from 'src/SharedComponents/UnlabeledMultipleChoiceQuestion';

import { getAnswers, saveAnswer, submitForm } from 'src/service';
import { SessionContext } from 'src/Views/SessionContext';

interface ILabelContainerProps {
    isScrolled: boolean
};

const FixedContainer = styled.div`
    position: fixed;
    top: ${(props: ILabelContainerProps) => props.isScrolled ? "0" : "10px"};
    left: 10px;
    background: #005eb8;
    width: calc(100% - 20px);
`;

const LabelContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    padding: 12px;
    line-height: 1;
`;

const FixedPadding = styled.div`
    height: 144px;
`;

const Label = styled.p`
    height: 140px;
    width: 24px;
    margin: 0 6px;
    writing-mode: vertical-rl;
    transform: rotate(-180deg);
    font-size: 1.1rem;
    font-weight: 600;
`;

const ToastButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const IncompleteToast = styled.div`
    position: fixed;
    bottom: 12px;
    left: -300px;
    border-radius: 16px;
    padding: 12px;
    color: white;
    border: 1px solid white;
    background-color: #005eb8;
    font-size: 1rem;
    transition: left 500ms;
    visibility: hidden;
    margin-right: 24px;
    &.sliding {
        left: 24px;
        visibility: visible;
    }
`;

const answers = ["Not at all", "Only occasionally", "Sometimes", "Often", "Most or all of the time"];
const QUESTION_IDS = [153, 154, 156, 157, 158, 159, 160, 161, 162, 163];

interface IProps {
	token: string;
};

const CorePage = (props: IProps) => {

	const sessionContext = React.useContext(SessionContext);
    const { webToken } = sessionContext.state;
	const [loading, setLoading] = React.useState(false);
    const [incomplete, setIncomplete] = React.useState(false);
	const [error, setError] = React.useState("");
	const [isScrolled, setIsScrolled] = React.useState(false);
	const { dispatch, state } = React.useContext(SessionContext);
	const { answered, numberAnswered, updateAnswered, updateAllAnswered, getAnswered, ERROR_SAVING_MESSAGE } = useProgressBar();
	const questionRefs = QUESTION_IDS.map(questionId => React.useRef(null as unknown as HTMLLIElement));
    
    React.useEffect(() => {
        if (webToken) {
            getAnswers(props.token, webToken, (submissionResponse: ISubmission) => {
                const questionAnswers = submissionResponse.answers.map(a => {
                    return {
                        isAnswered: true,
                        itemIndex: answers.indexOf(a.answer),
                        questionId: a.questionId
                    };
                });
                updateAllAnswered(questionAnswers);
            }, (aError: AxiosError) => {
                setError("There was an error loading previous answers. Please fill in all questions.");
            });
        }
    }, [webToken]);

    const checkUnauthorised = (serverError: AxiosError) => {
        if (serverError.response && serverError.response.data.code === 401) {
            dispatch({ type: "clearWebToken", payload: undefined });
        }
    }

    const handleError = (serverError: AxiosError, questionId: number) => {
        checkUnauthorised(serverError);
        setError(ERROR_SAVING_MESSAGE)
        updateAnswered(questionId, false, -1);
    };

    const handleSubmissionError = (serverError: AxiosError) => {
        checkUnauthorised(serverError);
        if (serverError.response && serverError.response.status === 409) {
            setLoading(false);
        }
        else {
            setError(ERROR_SAVING_MESSAGE)
        }
    };

    const onAnswerChange = (questionId: number, answer: string, itemIndex?: number) => {       
        checkEnoughAnswered()
        setError("");
        saveAnswer(questionId, answer, state.webToken, () => {
            updateAnswered(questionId, !!answer, itemIndex !== undefined ? itemIndex : -1);
        }, handleError)
    };

    React.useEffect(() => {
        const updateScrolled = () => {
            setIsScrolled(window.pageYOffset > 0);
        };

        window.addEventListener('scroll', updateScrolled);
        return () => {
            window.removeEventListener('scroll', updateScrolled);
        };
    }, []);

    const checkEnoughAnswered = () => {
        if(answered.length < 9) {
            return false
        }

        return true
    };

    const cancelSubmit = () => {
        setIncomplete(false);
    };

    const checkSubmit = () => {
        if(answered.length < 10) {
            setIncomplete(true)
        } else {
            onSubmit()
        }
    }
    
    const onSubmit = () => {
        setLoading(true);
        submitForm(state.webToken, () => {
            dispatch({ type: "setPage", payload: { formId: 4, page: 12 } });
        }, handleSubmissionError);
    };

    return <ContentWrapper>
    	<FixedPadding />
	<FixedContainer isScrolled={isScrolled}> 
		<LabelContainer>
			<Label>Not at all</Label>
			<Label>Only occasionally</Label>
			<Label>Sometimes</Label>
			<Label>Often</Label>
            <Label>Most or all of the time</Label>
		</LabelContainer>
	</FixedContainer>
	<QuestionList>
			<UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(153)} refProp={questionRefs[0]} question='I have felt tense, anxious or nervous' answers={answers} questionId={153} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(154)} refProp={questionRefs[1]} question='I have felt I have someone to turn to for support when needed' answers={answers} questionId={154} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(155)} refProp={questionRefs[2]} question='I have felt able to cope when things go wrong' answers={answers} questionId={155} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(156)} refProp={questionRefs[3]} question='Talking to people has felt too much for me' answers={answers} questionId={156} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(157)} refProp={questionRefs[4]} question='I have felt panic or terror' answers={answers} questionId={157} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(158)} refProp={questionRefs[5]} question='I made plans to end my life'answers={answers} questionId={158} onAnswerChange={onAnswerChange}/>
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(159)} refProp={questionRefs[6]} question='I have had difficulty getting to sleep or staying asleep' answers={answers} questionId={159} onAnswerChange={onAnswerChange}/>
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(160)} refProp={questionRefs[7]} question='I have felt despairing or hopeless' answers={answers} questionId={160} onAnswerChange={onAnswerChange}/>
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(161)} refProp={questionRefs[8]} question='I have felt unhappy' answers={answers} questionId={161} onAnswerChange={onAnswerChange}/>
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(162)} refProp={questionRefs[9]} question='Unwanted images or memories have been distressing me' answers={answers} questionId={162} onAnswerChange={onAnswerChange}/>
	</QuestionList>
    <ButtonContainer>
            {loading && <LoadingIndicator />}
            {!loading && <Button text="Submit answers" onClick={checkSubmit} onDisabledClick={() => setError("Please answer a minimum of 9 questions.")} disabled={!checkEnoughAnswered()} />}
        </ButtonContainer>
        <ProgressBar length={47} progress={numberAnswered} />
        <ErrorToast role="alert" aria-atomic="true" className={error ? "sliding" : "hidden"}>{error}</ErrorToast>
        <IncompleteToast className={incomplete ? "sliding" : "hidden"}>
            You have not completed one or more of the questions.  Are you sure you want to submit this outcome measure?
            <ToastButtonContainer>
                <Button text="Cancel" onClick={cancelSubmit} />
                <Button text="Save" onClick={onSubmit} />
            </ToastButtonContainer>
        </IncompleteToast>
        <AttributionContainer>
            © CORE System Trust: https://www.coresystemtrust.org.uk/copyright.pdf
        </AttributionContainer>
    </ContentWrapper>
}

export default CorePage;



// WEBPACK FOOTER //
// ./src/Forms/core/CorePage.tsx